import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  LinearProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  PlayArrow as PlayIcon,
  Refresh as RefreshIcon,
  FolderOpen as FolderIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';

interface FlowMetadata {
  id: string;
  name: string;
  description: string;
  createdAt: string;
  updatedAt: string;
  currentStage: number;
  completedStages: number[];
  status: 'created' | 'in_progress' | 'completed';
  tags: string[];
}

interface FlowSelectorProps {
  onSelectFlow: (flowId: string) => void;
  onCreateNew: (flowId: string) => void;
}

const FlowSelector: React.FC<FlowSelectorProps> = ({ onSelectFlow, onCreateNew }) => {
  const [flows, setFlows] = useState<FlowMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unauthorized, setUnauthorized] = useState<boolean>(false);
  const [authorizedConfirmed, setAuthorizedConfirmed] = useState<boolean>(false);
  const [initialized, setInitialized] = useState<boolean>(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newFlowName, setNewFlowName] = useState('');
  const [newFlowDescription, setNewFlowDescription] = useState('');

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  useEffect(() => {
    loadFlows();
  }, []);

  const loadFlows = async () => {
    setLoading(true);
    setError(null);
    try {
      const userEmail = localStorage.getItem('userEmail') || '';
      const headers: Record<string, string> = {};
      if (userEmail) {
        headers['X-User-Email'] = userEmail;
      }
      const response = await fetch(`${API_BASE_URL}/api/flows`, { headers });
      if (!response.ok) {
        if (response.status === 403) {
          setUnauthorized(true);
          throw new Error('Not authorized. Please use an approved email.');
        }
        throw new Error('Failed to load flows');
      }
      const data = await response.json();
      setFlows(data.flows || []);
      setAuthorizedConfirmed(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load flows');
      console.error('Error loading flows:', err);
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  };

  const createFlow = async () => {
    if (!newFlowName.trim()) {
      setError('Flow name is required');
      return;
    }

    try {
      const userEmail = localStorage.getItem('userEmail') || '';
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (userEmail) {
        headers['X-User-Email'] = userEmail;
      }
      const response = await fetch(`${API_BASE_URL}/api/flows`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          name: newFlowName,
          description: newFlowDescription,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create flow');
      }

      const data = await response.json();
      setCreateDialogOpen(false);
      setNewFlowName('');
      setNewFlowDescription('');
      onCreateNew(data.flow_id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create flow');
      console.error('Error creating flow:', err);
    }
  };

  const deleteFlow = async (flowId: string) => {
    if (!window.confirm('Are you sure you want to delete this flow?')) {
      return;
    }

    try {
      const userEmail = localStorage.getItem('userEmail') || '';
      const headers: Record<string, string> = {};
      if (userEmail) {
        headers['X-User-Email'] = userEmail;
      }
      const response = await fetch(`${API_BASE_URL}/api/flows/${flowId}`, {
        method: 'DELETE',
        headers,
      });

      if (!response.ok) {
        throw new Error('Failed to delete flow');
      }

      // Reload flows after deletion
      loadFlows();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete flow');
      console.error('Error deleting flow:', err);
    }
  };

  const duplicateFlow = async (flowId: string) => {
    const flow = flows.find(f => f.id === flowId);
    if (!flow) return;

    try {
      const userEmail = localStorage.getItem('userEmail') || '';
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (userEmail) {
        headers['X-User-Email'] = userEmail;
      }
      const response = await fetch(`${API_BASE_URL}/api/flows/${flowId}/duplicate`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          new_name: `${flow.name} (Copy)`,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to duplicate flow');
      }

      // Reload flows after duplication
      loadFlows();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to duplicate flow');
      console.error('Error duplicating flow:', err);
    }
  };

  const getProgressPercentage = (flow: FlowMetadata) => {
    return (flow.completedStages.length / 7) * 100;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'primary';
      default:
        return 'default';
    }
  };

  // Avoid any UI flash before auth state is known
  if (!initialized) {
    return <Container maxWidth="xl" sx={{ py: 4 }} />;
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {authorizedConfirmed && (
        <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h4" component="h1">
            FAR Procurement Workflows
          </Typography>
          <Box>
            <IconButton onClick={loadFlows} color="primary">
              <RefreshIcon />
            </IconButton>
            {!unauthorized && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateDialogOpen(true)}
                sx={{ ml: 1 }}
              >
                New Workflow
              </Button>
            )}
          </Box>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Select an existing workflow to continue or create a new one
        </Typography>
      </Paper>
      )}

      {error && (
        <Alert severity={unauthorized ? 'warning' : 'error'} sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
          {unauthorized && (
            <>
              {' '}
              <Button size="small" sx={{ ml: 2 }} onClick={() => { localStorage.removeItem('userEmail'); window.location.reload(); }}>
                Change Email
              </Button>
            </>
          )}
        </Alert>
      )}

      {authorizedConfirmed ? (
        loading ? (
          <Paper sx={{ p: 3 }}>
            <LinearProgress />
            <Typography align="center" sx={{ mt: 2 }}>
              Loading workflows...
            </Typography>
          </Paper>
        ) : flows.length === 0 ? (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <FolderIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No workflows found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Create your first workflow to get started
            </Typography>
            {!unauthorized && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateDialogOpen(true)}
              >
                Create First Workflow
              </Button>
            )}
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {flows.map((flow) => (
              <Grid item xs={12} sm={6} md={4} key={flow.id}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {flow.name}
                    </Typography>
                    {flow.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {flow.description}
                      </Typography>
                    )}
                    
                    <Box sx={{ mb: 2 }}>
                      <Chip
                        label={flow.status.replace('_', ' ')}
                        color={getStatusColor(flow.status) as any}
                        size="small"
                        sx={{ mr: 1 }}
                      />
                      <Chip
                        label={`Stage ${flow.currentStage}/7`}
                        variant="outlined"
                        size="small"
                      />
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Progress: {flow.completedStages.length}/7 stages
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={getProgressPercentage(flow)}
                        sx={{ mt: 1 }}
                      />
                    </Box>

                    <Typography variant="caption" display="block" color="text.secondary">
                      Created: {format(new Date(flow.createdAt), 'MMM d, yyyy h:mm a')}
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary">
                      Updated: {format(new Date(flow.updatedAt), 'MMM d, yyyy h:mm a')}
                    </Typography>
                  </CardContent>
                  
                  <CardActions>
                    <Button
                      size="small"
                      startIcon={<PlayIcon />}
                      onClick={() => onSelectFlow(flow.id)}
                      color="primary"
                    >
                      Continue
                    </Button>
                    <Tooltip title="Duplicate">
                      <IconButton
                        size="small"
                        onClick={() => duplicateFlow(flow.id)}
                      >
                        <CopyIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => deleteFlow(flow.id)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )
      ) : null}

      {/* Create Flow Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Workflow</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Workflow Name"
            fullWidth
            variant="outlined"
            value={newFlowName}
            onChange={(e) => setNewFlowName(e.target.value)}
            placeholder="e.g., Laptop Procurement Q1 2025"
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description (Optional)"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={newFlowDescription}
            onChange={(e) => setNewFlowDescription(e.target.value)}
            placeholder="e.g., Procurement of 15-inch business laptops for Air Force"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={createFlow} variant="contained">
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default FlowSelector;
